import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Settings,
  DollarSign,
  Bell,
  Shield,
  Globe,
  Mail,
  Save,
  RefreshCw,
  Building,
  Users,
  FileText,
  ToggleLeft,
  CreditCard,
  History,
  Upload,
  AlertTriangle,
  Check,
  X,
  Eye,
  EyeOff,
  Loader2,
  RotateCcw,
  Send
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { settingsAPI } from '../../services/api';
import { format } from 'date-fns';

// Section Card Component
const SettingsSection = ({ icon: Icon, title, description, color = 'blue', children }) => (
  <Card className="p-6">
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <h2 className="text-lg font-outfit font-bold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
    {children}
  </Card>
);

// Toggle Setting Row
const ToggleSetting = ({ label, description, checked, onChange, disabled = false }) => (
  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
    <div className="flex-1">
      <p className="font-medium text-gray-900">{label}</p>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
  </div>
);

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('platform');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [countriesList, setCountriesList] = useState([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetSection, setResetSection] = useState(null);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Fetch settings on mount
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch settings history
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const response = await settingsAPI.getHistory({ limit: 20 });
      if (response.success) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Fetch countries list
  const fetchCountriesList = useCallback(async () => {
    try {
      const response = await settingsAPI.getCountriesList();
      if (response.success) {
        setCountriesList(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchCountriesList();
  }, [fetchSettings, fetchCountriesList]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // Save section
  const handleSaveSection = async (section) => {
    try {
      setSaving(true);
      const response = await settingsAPI.updateSection(section, settings[section]);
      if (response.success) {
        toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved`);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Reset section
  const handleReset = async () => {
    try {
      setSaving(true);
      const response = await settingsAPI.reset(resetSection);
      if (response.success) {
        toast.success(resetSection ? `${resetSection} settings reset to defaults` : 'All settings reset to defaults');
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to reset settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
      setResetDialogOpen(false);
      setResetSection(null);
    }
  };

  // Test email
  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      setSendingTestEmail(true);
      const response = await settingsAPI.testEmail(testEmail);
      if (response.success) {
        toast.success('Test email sent successfully');
        setTestEmailDialogOpen(false);
        setTestEmail('');
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.error || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Upload logo
  const handleUploadLogo = async () => {
    if (!logoFile) return;
    try {
      setUploadingLogo(true);
      const response = await settingsAPI.uploadLogo(logoFile);
      if (response.success) {
        toast.success('Logo uploaded successfully');
        setSettings(prev => ({
          ...prev,
          platform: { ...prev.platform, logoUrl: response.data.logoUrl }
        }));
        setLogoFile(null);
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Update nested setting
  const updateSetting = (section, path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings[section];

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return newSettings;
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!settings) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Failed to load settings</p>
            <Button onClick={fetchSettings} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-7 h-7 text-primary" />
              Platform Settings
            </h1>
            <p className="text-gray-600 mt-1">Manage platform configuration and preferences</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setResetSection(null);
                setResetDialogOpen(true);
              }}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset All
            </Button>
            <Button onClick={fetchSettings} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="platform" className="gap-1.5">
              <Building className="w-4 h-4" />
              <span className="hidden sm:inline">Platform</span>
            </TabsTrigger>
            <TabsTrigger value="commission" className="gap-1.5">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Commission</span>
            </TabsTrigger>
            <TabsTrigger value="applications" className="gap-1.5">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Applications</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-1.5">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-1.5">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-1.5">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="gap-1.5">
              <ToggleLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Features</span>
            </TabsTrigger>
            <TabsTrigger value="payment" className="gap-1.5">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Payment</span>
            </TabsTrigger>
            <TabsTrigger value="countries" className="gap-1.5">
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">Countries</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
          </TabsList>

          {/* Platform Settings */}
          <TabsContent value="platform" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={Building}
                title="Platform Profile"
                description="Basic platform information and branding"
                color="purple"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="platformName">Platform Name</Label>
                    <Input
                      id="platformName"
                      value={settings.platform?.name || ''}
                      onChange={(e) => updateSetting('platform', 'name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tagline">Tagline</Label>
                    <Input
                      id="tagline"
                      value={settings.platform?.tagline || ''}
                      onChange={(e) => updateSetting('platform', 'tagline', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactEmail">Contact Email</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={settings.platform?.contactEmail || ''}
                      onChange={(e) => updateSetting('platform', 'contactEmail', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supportEmail">Support Email</Label>
                    <Input
                      id="supportEmail"
                      type="email"
                      value={settings.platform?.supportEmail || ''}
                      onChange={(e) => updateSetting('platform', 'supportEmail', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supportPhone">Support Phone</Label>
                    <Input
                      id="supportPhone"
                      value={settings.platform?.supportPhone || ''}
                      onChange={(e) => updateSetting('platform', 'supportPhone', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Website URL</Label>
                    <Input
                      id="website"
                      value={settings.platform?.website || ''}
                      onChange={(e) => updateSetting('platform', 'website', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Textarea
                      id="address"
                      value={settings.platform?.address || ''}
                      onChange={(e) => updateSetting('platform', 'address', e.target.value)}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="mt-6 p-4 border rounded-lg bg-stone-50">
                  <Label>Platform Logo</Label>
                  <div className="flex items-center gap-4 mt-2">
                    {settings.platform?.logoUrl && (
                      <img
                        src={settings.platform.logoUrl}
                        alt="Platform Logo"
                        className="w-16 h-16 object-contain rounded border bg-white"
                      />
                    )}
                    <div className="flex-1">
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={(e) => setLogoFile(e.target.files[0])}
                        className="mb-2"
                      />
                      <p className="text-xs text-gray-500">PNG, JPEG, SVG or WebP. Max 2MB.</p>
                    </div>
                    {logoFile && (
                      <Button onClick={handleUploadLogo} disabled={uploadingLogo} size="sm">
                        {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Social Links */}
                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-3">Social Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="facebook">Facebook</Label>
                      <Input
                        id="facebook"
                        value={settings.platform?.socialLinks?.facebook || ''}
                        onChange={(e) => updateSetting('platform', 'socialLinks.facebook', e.target.value)}
                        placeholder="https://facebook.com/..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="twitter">Twitter / X</Label>
                      <Input
                        id="twitter"
                        value={settings.platform?.socialLinks?.twitter || ''}
                        onChange={(e) => updateSetting('platform', 'socialLinks.twitter', e.target.value)}
                        placeholder="https://twitter.com/..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="linkedin">LinkedIn</Label>
                      <Input
                        id="linkedin"
                        value={settings.platform?.socialLinks?.linkedin || ''}
                        onChange={(e) => updateSetting('platform', 'socialLinks.linkedin', e.target.value)}
                        placeholder="https://linkedin.com/company/..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="instagram">Instagram</Label>
                      <Input
                        id="instagram"
                        value={settings.platform?.socialLinks?.instagram || ''}
                        onChange={(e) => updateSetting('platform', 'socialLinks.instagram', e.target.value)}
                        placeholder="https://instagram.com/..."
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('platform')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Platform Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Commission Settings */}
          <TabsContent value="commission" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={DollarSign}
                title="Commission Settings"
                description="Configure default commission rates and payout rules"
                color="green"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="defaultAgentCommission">Default Agent Commission (%)</Label>
                    <Input
                      id="defaultAgentCommission"
                      type="number"
                      min="0"
                      max="100"
                      value={settings.commission?.defaultAgentCommission || 0}
                      onChange={(e) => updateSetting('commission', 'defaultAgentCommission', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Applied to all new agents</p>
                  </div>
                  <div>
                    <Label htmlFor="defaultCounselorCommission">Default Counselor Commission (%)</Label>
                    <Input
                      id="defaultCounselorCommission"
                      type="number"
                      min="0"
                      max="100"
                      value={settings.commission?.defaultCounselorCommission || 0}
                      onChange={(e) => updateSetting('commission', 'defaultCounselorCommission', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Applied to all new counselors</p>
                  </div>
                  <div>
                    <Label htmlFor="minCommission">Minimum Commission (%)</Label>
                    <Input
                      id="minCommission"
                      type="number"
                      min="0"
                      max="100"
                      value={settings.commission?.minCommission || 0}
                      onChange={(e) => updateSetting('commission', 'minCommission', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxCommission">Maximum Commission (%)</Label>
                    <Input
                      id="maxCommission"
                      type="number"
                      min="0"
                      max="100"
                      value={settings.commission?.maxCommission || 0}
                      onChange={(e) => updateSetting('commission', 'maxCommission', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="payoutThreshold">Payout Threshold</Label>
                    <Input
                      id="payoutThreshold"
                      type="number"
                      min="0"
                      value={settings.commission?.payoutThreshold || 0}
                      onChange={(e) => updateSetting('commission', 'payoutThreshold', parseFloat(e.target.value))}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum amount required for payout</p>
                  </div>
                  <div>
                    <Label htmlFor="payoutFrequency">Payout Frequency</Label>
                    <Select
                      value={settings.commission?.payoutFrequency || 'monthly'}
                      onValueChange={(value) => updateSetting('commission', 'payoutFrequency', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="on_request">On Request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="commissionCurrency">Currency</Label>
                    <Select
                      value={settings.commission?.commissionCurrency || 'USD'}
                      onValueChange={(value) => updateSetting('commission', 'commissionCurrency', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR ()</SelectItem>
                        <SelectItem value="GBP">GBP ()</SelectItem>
                        <SelectItem value="INR">INR ()</SelectItem>
                        <SelectItem value="AUD">AUD ($)</SelectItem>
                        <SelectItem value="CAD">CAD ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4">
                  <ToggleSetting
                    label="Auto-approve Commissions"
                    description="Automatically approve commissions without manual review"
                    checked={settings.commission?.autoApproveCommissions || false}
                    onChange={(value) => updateSetting('commission', 'autoApproveCommissions', value)}
                  />
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('commission')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Commission Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Application Rules */}
          <TabsContent value="applications" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={FileText}
                title="Application Rules"
                description="Configure default application rules and document requirements"
                color="blue"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="maxApplications">Max Applications Per Student</Label>
                    <Input
                      id="maxApplications"
                      type="number"
                      min="1"
                      value={settings.applicationRules?.maxApplicationsPerStudent || 10}
                      onChange={(e) => updateSetting('applicationRules', 'maxApplicationsPerStudent', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="deadlineDays">Application Deadline (days)</Label>
                    <Input
                      id="deadlineDays"
                      type="number"
                      min="1"
                      value={settings.applicationRules?.applicationDeadlineDays || 30}
                      onChange={(e) => updateSetting('applicationRules', 'applicationDeadlineDays', parseInt(e.target.value))}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Days to complete application after starting</p>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <ToggleSetting
                    label="Allow Late Applications"
                    description="Allow applications after deadline has passed"
                    checked={settings.applicationRules?.allowLateApplications || false}
                    onChange={(value) => updateSetting('applicationRules', 'allowLateApplications', value)}
                  />
                  <ToggleSetting
                    label="Require Document Verification"
                    description="All documents must be verified before processing"
                    checked={settings.applicationRules?.requireDocumentVerification ?? true}
                    onChange={(value) => updateSetting('applicationRules', 'requireDocumentVerification', value)}
                  />
                  <ToggleSetting
                    label="Auto-assign Counselor"
                    description="Automatically assign available counselor to new applications"
                    checked={settings.applicationRules?.autoAssignCounselor || false}
                    onChange={(value) => updateSetting('applicationRules', 'autoAssignCounselor', value)}
                  />
                  <ToggleSetting
                    label="Auto-assign Agent"
                    description="Automatically assign agent based on referral"
                    checked={settings.applicationRules?.autoAssignAgent || false}
                    onChange={(value) => updateSetting('applicationRules', 'autoAssignAgent', value)}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-3">Required Documents</h3>
                  <div className="flex flex-wrap gap-2">
                    {['transcripts', 'passport', 'sop', 'recommendation', 'resume', 'testScores'].map(doc => (
                      <Badge
                        key={doc}
                        variant={settings.applicationRules?.requiredDocuments?.includes(doc) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const current = settings.applicationRules?.requiredDocuments || [];
                          const updated = current.includes(doc)
                            ? current.filter(d => d !== doc)
                            : [...current, doc];
                          updateSetting('applicationRules', 'requiredDocuments', updated);
                        }}
                      >
                        {doc.charAt(0).toUpperCase() + doc.slice(1).replace(/([A-Z])/g, ' $1')}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Click to toggle required status</p>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('applicationRules')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Application Rules
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* User & Role Settings */}
          <TabsContent value="users" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={Users}
                title="User & Role Settings"
                description="Configure user registration and role permissions"
                color="indigo"
              >
                <div className="space-y-4">
                  <ToggleSetting
                    label="Allow Self Registration"
                    description="Allow users to register accounts without invitation"
                    checked={settings.userRoles?.allowSelfRegistration ?? true}
                    onChange={(value) => updateSetting('userRoles', 'allowSelfRegistration', value)}
                  />
                  <ToggleSetting
                    label="Require Email Verification"
                    description="Users must verify email before accessing platform"
                    checked={settings.userRoles?.requireEmailVerification ?? true}
                    onChange={(value) => updateSetting('userRoles', 'requireEmailVerification', value)}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-3">Require Admin Approval</h3>
                  <div className="space-y-3">
                    <ToggleSetting
                      label="Students"
                      description="New student accounts require admin approval"
                      checked={settings.userRoles?.requireAdminApproval?.student || false}
                      onChange={(value) => updateSetting('userRoles', 'requireAdminApproval.student', value)}
                    />
                    <ToggleSetting
                      label="Agents"
                      description="New agent accounts require admin approval"
                      checked={settings.userRoles?.requireAdminApproval?.agent ?? true}
                      onChange={(value) => updateSetting('userRoles', 'requireAdminApproval.agent', value)}
                    />
                    <ToggleSetting
                      label="Counselors"
                      description="New counselor accounts require admin approval"
                      checked={settings.userRoles?.requireAdminApproval?.counselor ?? true}
                      onChange={(value) => updateSetting('userRoles', 'requireAdminApproval.counselor', value)}
                    />
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="maxStudentsPerCounselor">Max Students Per Counselor</Label>
                    <Input
                      id="maxStudentsPerCounselor"
                      type="number"
                      min="1"
                      value={settings.userRoles?.maxStudentsPerCounselor || 50}
                      onChange={(e) => updateSetting('userRoles', 'maxStudentsPerCounselor', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxStudentsPerAgent">Max Students Per Agent</Label>
                    <Input
                      id="maxStudentsPerAgent"
                      type="number"
                      min="1"
                      value={settings.userRoles?.maxStudentsPerAgent || 100}
                      onChange={(e) => updateSetting('userRoles', 'maxStudentsPerAgent', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('userRoles')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save User Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Email & Notification Settings */}
          <TabsContent value="email" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={Mail}
                title="Email & Notifications"
                description="Configure email delivery and notification preferences"
                color="cyan"
              >
                <div className="space-y-4">
                  <ToggleSetting
                    label="Enable Email Notifications"
                    description="Send email notifications to users"
                    checked={settings.emailNotifications?.enabled ?? true}
                    onChange={(value) => updateSetting('emailNotifications', 'enabled', value)}
                  />
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="emailProvider">Email Provider</Label>
                    <Select
                      value={settings.emailNotifications?.provider || 'resend'}
                      onValueChange={(value) => updateSetting('emailNotifications', 'provider', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resend">Resend</SelectItem>
                        <SelectItem value="sendgrid">SendGrid</SelectItem>
                        <SelectItem value="smtp">SMTP</SelectItem>
                        <SelectItem value="ses">Amazon SES</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="fromName">From Name</Label>
                    <Input
                      id="fromName"
                      value={settings.emailNotifications?.fromName || ''}
                      onChange={(e) => updateSetting('emailNotifications', 'fromName', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fromEmail">From Email</Label>
                    <Input
                      id="fromEmail"
                      type="email"
                      value={settings.emailNotifications?.fromEmail || ''}
                      onChange={(e) => updateSetting('emailNotifications', 'fromEmail', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="replyToEmail">Reply-To Email</Label>
                    <Input
                      id="replyToEmail"
                      type="email"
                      value={settings.emailNotifications?.replyToEmail || ''}
                      onChange={(e) => updateSetting('emailNotifications', 'replyToEmail', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">Notification Types</h3>
                    <Button variant="outline" size="sm" onClick={() => setTestEmailDialogOpen(true)}>
                      <Send className="w-4 h-4 mr-2" />
                      Send Test Email
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(settings.emailNotifications?.notifications || {}).map(([key, value]) => (
                      <ToggleSetting
                        key={key}
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        checked={value}
                        onChange={(newValue) => updateSetting('emailNotifications', `notifications.${key}`, newValue)}
                      />
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('emailNotifications')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Email Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={Shield}
                title="Security Settings"
                description="Configure password policy and session security"
                color="red"
              >
                <div className="space-y-6">
                  {/* Password Policy */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Password Policy</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="minLength">Minimum Length</Label>
                        <Input
                          id="minLength"
                          type="number"
                          min="6"
                          max="32"
                          value={settings.security?.passwordPolicy?.minLength || 8}
                          onChange={(e) => updateSetting('security', 'passwordPolicy.minLength', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="preventReuseCount">Prevent Reuse Count</Label>
                        <Input
                          id="preventReuseCount"
                          type="number"
                          min="0"
                          max="10"
                          value={settings.security?.passwordPolicy?.preventReuseCount || 3}
                          onChange={(e) => updateSetting('security', 'passwordPolicy.preventReuseCount', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <ToggleSetting
                        label="Require Uppercase"
                        checked={settings.security?.passwordPolicy?.requireUppercase ?? true}
                        onChange={(value) => updateSetting('security', 'passwordPolicy.requireUppercase', value)}
                      />
                      <ToggleSetting
                        label="Require Lowercase"
                        checked={settings.security?.passwordPolicy?.requireLowercase ?? true}
                        onChange={(value) => updateSetting('security', 'passwordPolicy.requireLowercase', value)}
                      />
                      <ToggleSetting
                        label="Require Numbers"
                        checked={settings.security?.passwordPolicy?.requireNumbers ?? true}
                        onChange={(value) => updateSetting('security', 'passwordPolicy.requireNumbers', value)}
                      />
                      <ToggleSetting
                        label="Require Special Characters"
                        checked={settings.security?.passwordPolicy?.requireSpecialChars || false}
                        onChange={(value) => updateSetting('security', 'passwordPolicy.requireSpecialChars', value)}
                      />
                    </div>
                  </div>

                  {/* Session Settings */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Session Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                        <Input
                          id="sessionTimeout"
                          type="number"
                          min="5"
                          value={settings.security?.sessionSettings?.sessionTimeout || 30}
                          onChange={(e) => updateSetting('security', 'sessionSettings.sessionTimeout', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="maxSessions">Max Concurrent Sessions</Label>
                        <Input
                          id="maxSessions"
                          type="number"
                          min="1"
                          value={settings.security?.sessionSettings?.maxConcurrentSessions || 3}
                          onChange={(e) => updateSetting('security', 'sessionSettings.maxConcurrentSessions', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="rememberMe">Remember Me Duration (days)</Label>
                        <Input
                          id="rememberMe"
                          type="number"
                          min="1"
                          value={settings.security?.sessionSettings?.rememberMeDuration || 7}
                          onChange={(e) => updateSetting('security', 'sessionSettings.rememberMeDuration', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Two-Factor Auth */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Two-Factor Authentication</h3>
                    <ToggleSetting
                      label="Enable Two-Factor Authentication"
                      description="Require 2FA for specified roles"
                      checked={settings.security?.twoFactorAuth?.enabled || false}
                      onChange={(value) => updateSetting('security', 'twoFactorAuth.enabled', value)}
                    />
                  </div>

                  {/* Login Attempts */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Login Protection</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="maxAttempts">Max Login Attempts</Label>
                        <Input
                          id="maxAttempts"
                          type="number"
                          min="3"
                          value={settings.security?.loginAttempts?.maxAttempts || 5}
                          onChange={(e) => updateSetting('security', 'loginAttempts.maxAttempts', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lockoutDuration">Lockout Duration (minutes)</Label>
                        <Input
                          id="lockoutDuration"
                          type="number"
                          min="5"
                          value={settings.security?.loginAttempts?.lockoutDuration || 15}
                          onChange={(e) => updateSetting('security', 'loginAttempts.lockoutDuration', parseInt(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Audit Logging */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Audit Logging</h3>
                    <ToggleSetting
                      label="Enable Audit Logging"
                      description="Track all significant actions on the platform"
                      checked={settings.security?.auditLogging?.enabled ?? true}
                      onChange={(value) => updateSetting('security', 'auditLogging.enabled', value)}
                    />
                    <div className="mt-3">
                      <Label htmlFor="retentionDays">Log Retention (days)</Label>
                      <Input
                        id="retentionDays"
                        type="number"
                        min="30"
                        value={settings.security?.auditLogging?.retentionDays || 90}
                        onChange={(e) => updateSetting('security', 'auditLogging.retentionDays', parseInt(e.target.value))}
                        className="mt-1 max-w-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('security')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Security Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Feature Toggles */}
          <TabsContent value="features" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={ToggleLeft}
                title="Feature Toggles"
                description="Enable or disable platform modules and features"
                color="orange"
              >
                {/* Modules */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Platform Modules</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(settings.features?.modules || {}).map(([key, value]) => (
                      <ToggleSetting
                        key={key}
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        checked={value}
                        onChange={(newValue) => updateSetting('features', `modules.${key}`, newValue)}
                      />
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-3">Additional Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(settings.features?.features || {}).map(([key, value]) => (
                      <ToggleSetting
                        key={key}
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        checked={value}
                        onChange={(newValue) => updateSetting('features', `features.${key}`, newValue)}
                      />
                    ))}
                  </div>
                </div>

                {/* Maintenance Mode */}
                <div className="mt-6 p-4 border rounded-lg bg-amber-50 border-amber-200">
                  <h3 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Maintenance Mode
                  </h3>
                  <ToggleSetting
                    label="Enable Maintenance Mode"
                    description="Only admins can access the platform when enabled"
                    checked={settings.features?.maintenance?.enabled || false}
                    onChange={(value) => updateSetting('features', 'maintenance.enabled', value)}
                  />
                  {settings.features?.maintenance?.enabled && (
                    <div className="mt-3">
                      <Label htmlFor="maintenanceMessage">Maintenance Message</Label>
                      <Textarea
                        id="maintenanceMessage"
                        value={settings.features?.maintenance?.message || ''}
                        onChange={(e) => updateSetting('features', 'maintenance.message', e.target.value)}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('features')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Feature Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Payment Settings */}
          <TabsContent value="payment" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={CreditCard}
                title="Payment Settings"
                description="Configure payment gateway and service fees"
                color="emerald"
              >
                <div className="space-y-4">
                  <ToggleSetting
                    label="Enable Payments"
                    description="Allow payments on the platform"
                    checked={settings.payment?.enabled || false}
                    onChange={(value) => updateSetting('payment', 'enabled', value)}
                  />
                </div>

                {settings.payment?.enabled && (
                  <>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="paymentGateway">Payment Gateway</Label>
                        <Select
                          value={settings.payment?.paymentGateway || 'none'}
                          onValueChange={(value) => updateSetting('payment', 'paymentGateway', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="stripe">Stripe</SelectItem>
                            <SelectItem value="razorpay">Razorpay</SelectItem>
                            <SelectItem value="paypal">PayPal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="paymentCurrency">Default Currency</Label>
                        <Select
                          value={settings.payment?.currency || 'USD'}
                          onValueChange={(value) => updateSetting('payment', 'currency', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="EUR">EUR ()</SelectItem>
                            <SelectItem value="GBP">GBP ()</SelectItem>
                            <SelectItem value="INR">INR ()</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Service Fees */}
                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-3">Service Fees</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(settings.payment?.serviceFees || {}).map(([key, value]) => (
                          <div key={key}>
                            <Label htmlFor={`fee-${key}`}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </Label>
                            <Input
                              id={`fee-${key}`}
                              type="number"
                              min="0"
                              value={value || 0}
                              onChange={(e) => updateSetting('payment', `serviceFees.${key}`, parseFloat(e.target.value))}
                              className="mt-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tax Settings */}
                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-3">Tax Settings</h3>
                      <ToggleSetting
                        label="Enable Tax"
                        checked={settings.payment?.taxSettings?.enabled || false}
                        onChange={(value) => updateSetting('payment', 'taxSettings.enabled', value)}
                      />
                      {settings.payment?.taxSettings?.enabled && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="taxName">Tax Name</Label>
                            <Input
                              id="taxName"
                              value={settings.payment?.taxSettings?.taxName || ''}
                              onChange={(e) => updateSetting('payment', 'taxSettings.taxName', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="taxRate">Tax Rate (%)</Label>
                            <Input
                              id="taxRate"
                              type="number"
                              min="0"
                              max="100"
                              value={settings.payment?.taxSettings?.taxRate || 0}
                              onChange={(e) => updateSetting('payment', 'taxSettings.taxRate', parseFloat(e.target.value))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="taxNumber">Tax Number</Label>
                            <Input
                              id="taxNumber"
                              value={settings.payment?.taxSettings?.taxNumber || ''}
                              onChange={(e) => updateSetting('payment', 'taxSettings.taxNumber', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Refund Policy */}
                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-3">Refund Policy</h3>
                      <ToggleSetting
                        label="Allow Refunds"
                        checked={settings.payment?.refundPolicy?.allowRefunds ?? true}
                        onChange={(value) => updateSetting('payment', 'refundPolicy.allowRefunds', value)}
                      />
                      {settings.payment?.refundPolicy?.allowRefunds && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="refundWindow">Refund Window (days)</Label>
                            <Input
                              id="refundWindow"
                              type="number"
                              min="0"
                              value={settings.payment?.refundPolicy?.refundWindowDays || 14}
                              onChange={(e) => updateSetting('payment', 'refundPolicy.refundWindowDays', parseInt(e.target.value))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="refundPercentage">Refund Percentage (%)</Label>
                            <Input
                              id="refundPercentage"
                              type="number"
                              min="0"
                              max="100"
                              value={settings.payment?.refundPolicy?.refundPercentage || 100}
                              onChange={(e) => updateSetting('payment', 'refundPolicy.refundPercentage', parseFloat(e.target.value))}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('payment')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Payment Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Country Settings */}
          <TabsContent value="countries" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <SettingsSection
                icon={Globe}
                title="Country Settings"
                description="Configure enabled countries and destinations"
                color="teal"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="defaultCountry">Default Country</Label>
                    <Select
                      value={settings.countries?.defaultCountry || 'India'}
                      onValueChange={(value) => updateSetting('countries', 'defaultCountry', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countriesList.map(country => (
                          <SelectItem key={country.code} value={country.name}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-3">Popular Destinations</h3>
                  <p className="text-sm text-gray-500 mb-3">Select countries to highlight as popular study destinations</p>
                  <div className="flex flex-wrap gap-2">
                    {countriesList.slice(0, 20).map(country => (
                      <Badge
                        key={country.code}
                        variant={settings.countries?.popularDestinations?.includes(country.name) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const current = settings.countries?.popularDestinations || [];
                          const updated = current.includes(country.name)
                            ? current.filter(c => c !== country.name)
                            : [...current, country.name];
                          updateSetting('countries', 'popularDestinations', updated);
                        }}
                      >
                        {country.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => handleSaveSection('countries')} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Country Settings
                  </Button>
                </div>
              </SettingsSection>
            </motion.div>
          </TabsContent>

          {/* Settings History */}
          <TabsContent value="history" className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <History className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-outfit font-bold text-gray-900">Settings Change History</h2>
                    <p className="text-sm text-gray-600">Track all settings modifications</p>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No settings changes recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((log, index) => (
                      <div
                        key={log.logId || index}
                        className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg"
                      >
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border">
                          <Settings className="w-5 h-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{log.actor}</span>
                            <Badge variant="outline" className="text-xs">
                              {log.action?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">
                            {log.details?.section && `Section: ${log.details.section}`}
                            {log.details?.updateType && ` (${log.details.updateType})`}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {log.timestamp && format(new Date(log.timestamp), 'PPpp')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Reset Settings
            </DialogTitle>
            <DialogDescription>
              {resetSection
                ? `Are you sure you want to reset ${resetSection} settings to their default values?`
                : 'Are you sure you want to reset ALL settings to their default values?'}
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReset} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Reset {resetSection || 'All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Enter an email address to receive a test email and verify your email configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="testEmailAddress">Email Address</Label>
            <Input
              id="testEmailAddress"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="your@email.com"
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleTestEmail} disabled={sendingTestEmail}>
              {sendingTestEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
